import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header Section */}
      <div className="relative pt-16 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">
              About CaseAbacus
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Understanding the mathematics behind CS2 case openings and probability calculations
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="space-y-8">
          {/* Introduction */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">What is CaseAbacus?</h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              CaseAbacus is an advanced probability calculator that helps you estimate how many CS2 cases 
              you need to open to get a specific item. We use real drop rates and mathematical formulas 
              to provide accurate estimates based on your desired confidence level.
            </p>
          </div>

          {/* Formula Section */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8">
            <h2 className="text-2xl font-bold mb-6 text-blue-400">The Mathematics</h2>
            <p className="text-lg mb-6 text-gray-300">
              We use the following formula to estimate the number of attempts needed to have a specific 
              chance (confidence level) of receiving an item with a given drop probability:
            </p>

            <div className="bg-gray-900/50 rounded-lg p-6 mb-6 border border-gray-600/50">
              <BlockMath math={"n = \\left\\lceil \\frac{\\ln(1 - c)}{\\ln(1 - p)} \\right\\rceil"} />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-600/50">
                <div className="text-sm text-gray-400 mb-1">Variable</div>
                <div className="text-lg font-bold text-orange-400"><InlineMath math="n" /></div>
                <div className="text-sm text-gray-300">Number of cases to open</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-600/50">
                <div className="text-sm text-gray-400 mb-1">Variable</div>
                <div className="text-lg font-bold text-blue-400"><InlineMath math="c" /></div>
                <div className="text-sm text-gray-300">Desired confidence level</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-600/50">
                <div className="text-sm text-gray-400 mb-1">Variable</div>
                <div className="text-lg font-bold text-green-400"><InlineMath math="p" /></div>
                <div className="text-sm text-gray-300">Drop chance of the item</div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8">
            <h2 className="text-2xl font-bold mb-6 text-green-400">How It Works</h2>
            <p className="text-lg mb-6 text-gray-300">
              We pull real-time data from the Steam Community Market to display cases and their contents. 
              Each item is categorized by rarity with specific drop chances. Our calculator provides:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Case Count Estimation</h3>
                    <p className="text-gray-400 text-sm">Calculate exactly how many cases you need to open</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Cost Analysis</h3>
                    <p className="text-gray-400 text-sm">See the total cost including case and key prices</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">StatTrak™ Odds</h3>
                    <p className="text-gray-400 text-sm">Estimate StatTrak™ drop rates (1/10 of base odds)</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Real-time Data</h3>
                    <p className="text-gray-400 text-sm">Live prices and odds from Steam Community Market</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Official Odds */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8">
            <h2 className="text-2xl font-bold mb-6 text-purple-400">Official CS2 Drop Rates</h2>
            <p className="text-lg mb-6 text-gray-300">
              These are the official odds published by Valve for CS2 case openings:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-blue-400">
                <div className="text-sm text-gray-400 mb-1">Blue (Mil-Spec)</div>
                <div className="text-2xl font-bold text-blue-400">79.92%</div>
                <div className="text-xs text-gray-400">4/5 chance</div>
              </div>
              
              <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-purple-400">
                <div className="text-sm text-gray-400 mb-1">Purple (Restricted)</div>
                <div className="text-2xl font-bold text-purple-400">15.98%</div>
                <div className="text-xs text-gray-400">1/6 chance</div>
              </div>
              
              <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-pink-400">
                <div className="text-sm text-gray-400 mb-1">Pink (Classified)</div>
                <div className="text-2xl font-bold text-pink-400">3.2%</div>
                <div className="text-xs text-gray-400">1/31 chance</div>
              </div>
              
              <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-red-400">
                <div className="text-sm text-gray-400 mb-1">Red (Covert)</div>
                <div className="text-2xl font-bold text-red-400">0.64%</div>
                <div className="text-xs text-gray-400">1/156 chance</div>
              </div>
              
              <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-yellow-400">
                <div className="text-sm text-gray-400 mb-1">Gold (Special)</div>
                <div className="text-2xl font-bold text-yellow-400">0.0026%</div>
                <div className="text-xs text-gray-400">1/385 chance</div>
              </div>
            </div>
            
            <p className="text-sm text-gray-400 mt-4">
              <strong>Note:</strong> Each rarity is approximately 5 times rarer than the previous one.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="bg-red-900/20 backdrop-blur-sm rounded-xl border border-red-700/50 p-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-red-400 mb-2">Important Disclaimer</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  This site is for educational and entertainment purposes only. It does not guarantee any 
                  real-world outcomes and is not affiliated with Valve or Steam. Gambling can be addictive 
                  and may result in financial loss. Please gamble responsibly and consider that opening 
                  cases is a form of gambling.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
